import { useState, useEffect } from 'react';
import { Input, Modal } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { BaseButton } from '../../../../components/UI/BaseButton';

interface ImportDeleteConfirmModalProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLoading: boolean;
  tasksCount?: number;
  createdByUser?: { fullName: string; profilePicturePath: string | null };
  createdAt?: string;
}

const CONFIRMATION_TEXT = 'DELETE IMPORT';

export const ImportDeleteConfirmModal = (props: ImportDeleteConfirmModalProps) => {
  const { open, onConfirm, onCancel, confirmLoading, createdByUser, createdAt } = props;
  const [inputValue, setInputValue] = useState('');

  const isConfirmEnabled = inputValue === CONFIRMATION_TEXT;

  useEffect(() => {
    setInputValue('');
  }, [open]);

  return (
    <Modal
      title={
        <div className="text-brand-grey-dark text-base font-semibold mx-4 mt-3 flex items-center gap-2">
          <span>Delete Import</span>
          {createdByUser && (
            <>
              <span className="font-normal text-neutral-500">by</span>
              {createdByUser.profilePicturePath ? (
                <img src={createdByUser.profilePicturePath} alt="" className="w-5 h-5 rounded-full" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-neutral-300 flex items-center justify-center text-[10px] text-neutral-700">
                  {createdByUser.fullName.charAt(0).toUpperCase()}
                </div>
              )}
              <span>{createdByUser.fullName}</span>
              {createdAt && (
                <span className="font-normal text-neutral-500">
                  · {dayjs(createdAt).format('MMM DD, YYYY')}
                </span>
              )}
            </>
          )}
        </div>
      }
      open={open}
      onCancel={onCancel}
      centered
      width={640}
      footer={
        <div className="flex items-center justify-between gap-2">
          <div
            className="flex items-center gap-2 text-brand-grey-dark text-sm font-semibold px-2 py-2 rounded-lg"
            style={{ backgroundColor: 'var(--color-yellow-100)' }}
          >
            <InfoCircleOutlined />
            <span>This action is irreversible.</span>
          </div>
          <div className="flex gap-2">
            <BaseButton type="secondary" size="medium" onClick={onCancel}>
              Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              size="medium"
              danger
              disabled={!isConfirmEnabled || confirmLoading}
              onClick={onConfirm}
            >
              Delete Import
            </BaseButton>
          </div>
        </div>
      }
    >
      <ul
        className="mx-4 mt-4 mb-4 text-brand-grey-dark text-sm list-disc list-inside space-y-1 p-5 rounded-lg"
        style={{ backgroundColor: 'var(--color-neutral-300)' }}
      >
        <li>All tasks created during this import will be permanently deleted.</li>
        <li>New labels created during this import will be permanently deleted and removed from all your tasks.</li>
      </ul>
      <p className="mx-4 mb-2 text-brand-grey-dark text-sm">
        To acknowledge that you agree with the terms, type &quot;DELETE IMPORT&quot; below.
      </p>
      <div className="mx-4 mb-6">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="DELETE IMPORT"
          className="text-sm"
        />
      </div>
    </Modal>
  );
};
